import { ConfirmModal } from "./ConfirmModal";
import { useEffect, useState } from "react";
import type { ScheduledPrompt, ScheduledPromptFrequency } from "../types";

interface Props {
  onClose: () => void;
  onOpenSession: (sessionId: string) => void;
}

interface FormState {
  title: string;
  prompt: string;
  frequency: ScheduledPromptFrequency;
  weekday: number;
  hourUtc: number;
}

const EMPTY_FORM: FormState = { title: "", prompt: "", frequency: "daily", weekday: 1, hourUtc: 9 };

const WEEKDAY_LABEL = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

function describeSchedule(sp: ScheduledPrompt): string {
  const hour = `${String(sp.hourUtc).padStart(2, "0")}:00 UTC`;
  return sp.frequency === "weekly" ? `toda ${WEEKDAY_LABEL[sp.weekday]}, ${hour}` : `todo dia, ${hour}`;
}

export function ScheduledPromptsModal({ onClose, onOpenSession }: Props) {
  const [items, setItems] = useState<ScheduledPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    fetch("/api/scheduled-prompts")
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j: { items: ScheduledPrompt[] }) => setItems(j.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !form) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, form]);

  const create = async () => {
    if (!form) return;
    if (!form.title.trim() || !form.prompt.trim()) {
      setError("título e prompt são obrigatórios");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch("/api/scheduled-prompts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setError(j.error ?? "falha ao criar o agendamento");
        return;
      }
      setForm(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (sp: ScheduledPrompt) => {
    setItems((prev) => prev.map((x) => (x.id === sp.id ? { ...x, active: !x.active } : x)));
    try {
      await fetch(`/api/scheduled-prompts/${encodeURIComponent(sp.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: !sp.active }),
      });
    } catch {
      /* ignore */
    } finally {
      load();
    }
  };

  const [toDelete, setToDelete] = useState<ScheduledPrompt | null>(null);
  const remove = async (sp: ScheduledPrompt) => {
    try {
      await fetch(`/api/scheduled-prompts/${encodeURIComponent(sp.id)}`, { method: "DELETE" });
    } catch {
      /* ignore */
    }
    load();
  };

  const runNow = async (sp: ScheduledPrompt) => {
    setRunningId(sp.id);
    try {
      const r = await fetch(`/api/scheduled-prompts/${encodeURIComponent(sp.id)}/run-now`, { method: "POST" });
      const j = await r.json().catch(() => ({}) as { error?: string; sessionId?: string });
      if (r.ok && j.sessionId) {
        onOpenSession(j.sessionId);
        onClose();
      } else {
        load();
      }
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal prompt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Perguntas agendadas">
        <header className="modal-header">
          <h2>Perguntas agendadas</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        {form ? (
          <div className="prompt-form">
            <input
              className="prompt-form-input"
              placeholder="Título (ex.: Resumo diário de pendências)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              autoFocus
              spellCheck={false}
            />
            <textarea
              className="prompt-form-textarea"
              placeholder="O que perguntar, toda vez que rodar…"
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              rows={5}
            />
            <div className="sched-form-row">
              <label>
                <span>Frequência</span>
                <select
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value as ScheduledPromptFrequency })}
                >
                  <option value="daily">Todo dia</option>
                  <option value="weekly">Toda semana</option>
                </select>
              </label>
              {form.frequency === "weekly" && (
                <label>
                  <span>Dia</span>
                  <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}>
                    {WEEKDAY_LABEL.map((w, i) => (
                      <option key={w} value={i}>
                        {w}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                <span>Hora (UTC)</span>
                <select value={form.hourUtc} onChange={(e) => setForm({ ...form, hourUtc: Number(e.target.value) })}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {error && <p className="prompt-form-error">{error}</p>}
            <div className="prompt-form-actions">
              <button type="button" className="action-btn" onClick={() => setForm(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="action-btn active" onClick={() => void create()} disabled={saving}>
                {saving ? "Salvando…" : "Criar agendamento"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="prompt-modal-toolbar">
              <p className="admin-user-meta" style={{ margin: 0 }}>
                Roda sozinho no horário escolhido e avisa quando terminar.
              </p>
              <button type="button" className="action-btn active" onClick={() => setForm({ ...EMPTY_FORM })}>
                + Novo
              </button>
            </div>
            <div className="prompt-list">
              {loading ? (
                <p className="prompt-list-status">Carregando…</p>
              ) : items.length === 0 ? (
                <p className="prompt-list-status">Nenhuma pergunta agendada ainda.</p>
              ) : (
                items.map((sp) => (
                  <div key={sp.id} className="sched-row">
                    <div className="sched-row-main">
                      <div className="sched-row-title-line">
                        <span className="prompt-row-title">{sp.title}</span>
                        <label className="sched-toggle">
                          <input type="checkbox" checked={sp.active} onChange={() => void toggleActive(sp)} />
                          {sp.active ? "ativo" : "pausado"}
                        </label>
                      </div>
                      <span className="prompt-row-body">{sp.prompt}</span>
                      <span className="sched-row-meta">
                        {describeSchedule(sp)}
                        {sp.lastRunAt && ` · última vez: ${new Date(sp.lastRunAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}`}
                        {sp.lastError && ` · erro: ${sp.lastError}`}
                      </span>
                    </div>
                    <div className="prompt-row-actions">
                      <button type="button" className="action-btn" disabled={runningId === sp.id} onClick={() => void runNow(sp)}>
                        {runningId === sp.id ? "Rodando…" : "Rodar agora"}
                      </button>
                      <button type="button" className="session-tool" onClick={() => setToDelete(sp)} title="Apagar">
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                          <path d="M3 4h10M6.5 4V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M5 4l.5 9a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1l.5-9" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
      {toDelete && (
        <ConfirmModal
          title="Apagar agendamento"
          message={`Apagar o agendamento "${toDelete.title}"?`}
          confirmLabel="Apagar"
          onConfirm={() => void remove(toDelete)}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  );
}
